import {
  Play, MessageSquare, Monitor, Bell, Zap, Users, GitBranch, Settings,
  ArrowRight, FolderOpen, ChevronDown, ChevronRight, Lock, Unlock, Layers,
  ArrowUpDown, FoldVertical, UnfoldVertical,
  MoreHorizontal,
  Search, X,
  // Preset icons
  Server, Router, Database, Cloud, Shield, Boxes,
  PlayCircle, Square, CheckSquare, Mail, Plug,
  User, Building2, Badge as BadgeIcon,
  HelpCircle, CheckCircle2, Lightbulb, Folder, Link as LinkIcon,
  Brain, Tag, StickyNote, Bookmark,
  Flag, GitFork, Calendar,
} from "lucide-react";
import { BlockType, WorkflowBlock, BlockGroup } from "@/types/workflow";
import { cn } from "@/lib/utils";
import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTranslation } from "@/i18n/LanguageContext";
import { LayerTree } from "./LayerTree";
import { useOnboarding } from "@/components/onboarding/useOnboarding";
import { getToolPreset } from "@/lib/toolPresets";
import { BRAND_NAVY } from "@/constants/brandColors";

// Icon resolver: supports both original keys ("play", "message", ...) and the
// lucide-style kebab-case names used by toolPresets ("play-circle", "git-branch").
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  // Legacy keys (sandbox/defaults)
  play: Play, message: MessageSquare, monitor: Monitor,
  bell: Bell, zap: Zap, users: Users, branch: GitBranch, settings: Settings,
  // Preset keys
  server: Server, router: Router, database: Database, cloud: Cloud,
  shield: Shield, boxes: Boxes,
  'play-circle': PlayCircle, square: Square, 'check-square': CheckSquare,
  mail: Mail, plug: Plug,
  user: User, 'building-2': Building2, badge: BadgeIcon,
  'help-circle': HelpCircle, 'git-branch': GitBranch, 'check-circle-2': CheckCircle2,
  lightbulb: Lightbulb, folder: Folder, link: LinkIcon,
  brain: Brain, tag: Tag, 'sticky-note': StickyNote, bookmark: Bookmark,
  flag: Flag, 'git-fork': GitFork, calendar: Calendar,
};

const PRESET_COLORS = [
  "#6366f1", "#8b5cf6", "#a855f7", "#d946ef", "#ec4899", "#f43f5e",
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e",
  "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9",
];

const NAVY = BRAND_NAVY;

interface ColorPickerProps { color: string; onChange: (color: string) => void; }

function ColorPicker({ color, onChange }: ColorPickerProps) {
  const { t } = useTranslation();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="w-6 h-6 rounded-md border border-brand-navy/20 flex-shrink-0 hover:scale-110 transition-transform"
          style={{ backgroundColor: color }}
          title={t("changeColor")}
          aria-label={t("changeColor")}
          data-testid="color-picker-trigger"
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" side="right" align="start">
        <div className="grid grid-cols-4 gap-1">
          {PRESET_COLORS.map((presetColor) => (
            <button
              key={presetColor}
              className={cn(
                "w-6 h-6 rounded-md hover:scale-110 transition-transform",
                color === presetColor && "ring-2 ring-brand-navy ring-offset-1 ring-offset-background",
              )}
              style={{ backgroundColor: presetColor }}
              onClick={() => onChange(presetColor)}
              aria-label={`Select color ${presetColor}`}
              aria-pressed={color === presetColor}
              data-testid={`color-preset-${presetColor}`}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface ToolSidebarProps {
  connectMode: boolean;
  groupMode: boolean;
  /** True when cross-sheet linking mode is active. Mutex with connect/group. */
  crossSheetMode?: boolean;
  /** True if the workspace has or can have multiple sheets (>1 sheet OR plan allows it). */
  multiSheet?: boolean;
  groupingCount: number;
  blocks: WorkflowBlock[];
  groups: BlockGroup[];
  selectedBlockId: string | null;
  currentLayerGroupId: string | null;
  onToggleConnectMode: () => void;
  onToggleGroupMode: () => void;
  onToggleCrossSheetMode?: () => void;
  onSelectBlock: (id: string) => void;
  onUpdateBlockColor: (id: string, color: string) => void;
  onUpdateGroupColor: (id: string, color: string) => void;
  onToggleGroupLock: (id: string) => void;
  onToggleGroupCollapse: (id: string) => void;
  onCollapseAll?: () => void;
  onCollapseToLayers?: () => void;
  onExpandAll?: () => void;
  onNavigateToLayer: (groupId: string | null) => void;
  mobile?: boolean;
  onMobileAddBlock?: (type: BlockType) => void;
}

export function ToolSidebar({
  connectMode, groupMode, crossSheetMode = false, multiSheet = false,
  groupingCount,
  blocks, groups, selectedBlockId, currentLayerGroupId,
  onToggleConnectMode, onToggleGroupMode, onToggleCrossSheetMode,
  onSelectBlock,
  onUpdateBlockColor, onUpdateGroupColor,
  onToggleGroupLock, onToggleGroupCollapse,
  onCollapseAll, onCollapseToLayers, onExpandAll,
  onNavigateToLayer, mobile, onMobileAddBlock,
}: ToolSidebarProps) {
  const { t, language } = useTranslation();
  const { progress } = useOnboarding();
  const useCase = progress?.preferences?.useCase ?? null;
  const [blocksOpen, setBlocksOpen] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(true);
  const [layersOpen, setLayersOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Tool list is now driven by the selected use-case preset.
  // Block type stays stable; only label + icon change per preset.
  const toolTypes = useMemo(() => {
    const preset = getToolPreset(useCase);
    return preset.map(p => ({
      type: p.type,
      icon: p.icon,
      label: p.label[language] ?? p.label.en,
    }));
  }, [useCase, language]);

  const normalizedQuery = searchQuery.toLowerCase().trim();

  const filteredTools = useMemo(() => {
    if (!normalizedQuery) return toolTypes;
    return toolTypes.filter((tool) =>
      tool.label.toLowerCase().includes(normalizedQuery) ||
      tool.type.toLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery, toolTypes]);

  const filteredBlocks = useMemo(() => {
    if (!normalizedQuery) return blocks;
    return blocks.filter((block) =>
      block.config.name.toLowerCase().includes(normalizedQuery) ||
      block.type.toLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery, blocks]);

  const filteredGroups = useMemo(() => {
    if (!normalizedQuery) return groups;
    return groups.filter((group) =>
      (group.name || "").toLowerCase().includes(normalizedQuery),
    );
  }, [normalizedQuery, groups]);

  const handleDragStart = (e: React.DragEvent, type: BlockType) => {
    if (mobile) return;
    e.dataTransfer.setData("blockType", type);
    e.dataTransfer.effectAllowed = "copy";

    const tool = toolTypes.find((tt) => tt.type === type);
    const label = tool ? tool.label : type;
    // Carry the preset label so the dropped block adopts the use-case name
    // (e.g. "Person"/"Server"/"Router") instead of the generic default
    // ("Aktivitet"). Consumed by WorkflowCanvas.handleDrop.
    e.dataTransfer.setData("toolLabel", label);
    const ghost = document.createElement("div");
    ghost.style.cssText =
      "position:fixed;left:-9999px;top:0;display:flex;align-items:center;gap:8px;" +
      `padding:8px 14px;border-radius:10px;border:2px solid ${NAVY};` +
      `background:#fff;color:${NAVY};font-size:13px;font-weight:600;` +
      "font-family:system-ui,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,0.15);" +
      "white-space:nowrap;pointer-events:none;z-index:9999;";
    ghost.textContent = label;
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight / 2);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  };

  const hasCollapsedGroups = groups.some((g) => g.collapsed);

  return (
    <aside
      className={cn("flex flex-col h-full", mobile ? "w-full" : "w-80")}
      role="complementary" aria-label="Workflow tools"
      data-testid="tool-sidebar"
      data-guide="tool-sidebar"
    >
      <div className={cn("border-b border-brand-navy/10", mobile ? "p-2" : "px-4 pt-3 pb-2")}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: NAVY, opacity: 0.4 }} aria-hidden="true" />
          <Input
            type="text"
            placeholder={t('search') || 'Search...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 pr-8 text-xs border-brand-navy/15 bg-white/60 placeholder:text-brand-navy/30 focus-visible:ring-brand-navy/30"
            style={{ color: NAVY }}
            data-testid="sidebar-search"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 transition-colors"
              style={{ color: NAVY, opacity: 0.4 }}
              aria-label="Clear search"
              data-testid="sidebar-search-clear"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className={cn("p-3", mobile ? "p-2" : "p-4", "border-b border-brand-navy/10")} data-guide="tool-sidebar-items">
          <h2 className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: NAVY, opacity: 0.5 }}>{t("tools")}</h2>
          <div className={cn("space-y-1", mobile && "space-y-0.5")}>
            {filteredTools.map((tool) => {
              const Icon = iconMap[tool.icon] ?? Settings;
              return (
                <div
                  key={`${tool.type}-${tool.label}`}
                  draggable={!mobile}
                  onDragStart={(e) => handleDragStart(e, tool.type)}
                  onClick={() => mobile && onMobileAddBlock?.(tool.type)}
                  className={cn(
                    "flex items-center gap-2 rounded-lg select-none transition-all duration-200",
                    mobile ? "px-2 py-1.5 cursor-pointer" : "px-2 py-2 cursor-grab active:cursor-grabbing",
                    "bg-brand-navy/5 hover:bg-brand-navy/10 hover:scale-[1.02]",
                  )}
                  style={{ color: NAVY }}
                >
                  <div className={cn("rounded-md flex items-center justify-center flex-shrink-0 bg-brand-navy/15", mobile ? "w-6 h-6" : "w-7 h-7")}>
                    <Icon className={cn(mobile ? "w-3 h-3" : "w-3.5 h-3.5", "text-brand-navy")} />
                  </div>
                  <span className={cn("font-medium truncate", mobile ? "text-[11px]" : "text-xs")}>{tool.label}</span>
                  {mobile && <span className="ml-auto text-[9px]" style={{ color: NAVY, opacity: 0.3 }}>+</span>}
                </div>
              );
            })}
          </div>
        </div>

        <div className={cn(mobile ? "p-2" : "p-4", "border-b border-brand-navy/10")}>
          <h2 className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: NAVY, opacity: 0.5 }}>{t("actions")}</h2>
          <div className={cn("space-y-1", mobile && "space-y-0.5")}>
            <button
              onClick={onToggleConnectMode}
              aria-pressed={connectMode}
              aria-label={connectMode ? t("connecting") : t("connectBlocks")}
              data-testid="toggle-connect-mode"
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg select-none transition-colors",
                connectMode ? "bg-destructive text-white" : "bg-brand-navy/5 hover:bg-brand-navy/10",
              )}
              style={connectMode ? undefined : { color: NAVY }}
            >
              <div className={cn("w-8 h-8 rounded-md flex items-center justify-center", connectMode ? "bg-white/20" : "bg-brand-navy/15")}>
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </div>
              <span className="text-sm font-medium">{connectMode ? t("connecting") : t("connectBlocks")}</span>
            </button>

            <button
              onClick={onToggleGroupMode}
              aria-pressed={groupMode}
              aria-label={groupMode ? `${t("grouping")} (${groupingCount})` : t("groupBlocks")}
              data-testid="toggle-group-mode"
              className={cn(
                "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg select-none transition-colors",
                groupMode ? "bg-destructive text-white" : "bg-brand-navy/5 hover:bg-brand-navy/10",
              )}
              style={groupMode ? undefined : { color: NAVY }}
            >
              <div className={cn("w-8 h-8 rounded-md flex items-center justify-center", groupMode ? "bg-white/20" : "bg-brand-navy/15")}>
                <FolderOpen className="w-4 h-4" aria-hidden="true" />
              </div>
              <span className="text-sm font-medium">
                {groupMode ? `${t("grouping")} (${groupingCount})` : t("groupBlocks")}
              </span>
            </button>

            {/* Cross-sheet linking — only shown when the workspace has or
                can have multiple sheets. Mutex with Connect/Group modes. */}
            {multiSheet && onToggleCrossSheetMode && (
              <button
                onClick={onToggleCrossSheetMode}
                aria-pressed={crossSheetMode}
                aria-label={crossSheetMode ? (t("crossSheetActive") || 'Cross-sheet linking') : (t("crossSheetLink") || 'Cross-sheet link')}
                data-testid="toggle-cross-sheet-mode"
                data-guide="toggle-cross-sheet"
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg select-none transition-colors",
                  crossSheetMode ? "bg-destructive text-white" : "bg-brand-navy/5 hover:bg-brand-navy/10",
                )}
                style={crossSheetMode ? undefined : { color: NAVY }}
                title={crossSheetMode
                  ? (t("crossSheetActiveHint") || 'Press Esc to cancel')
                  : (t("crossSheetHint") || 'Link blocks between adjacent sheets')}
              >
                <div className={cn("w-8 h-8 rounded-md flex items-center justify-center", crossSheetMode ? "bg-white/20" : "bg-brand-navy/15")}>
                  <ArrowUpDown className="w-4 h-4" aria-hidden="true" />
                </div>
                <span className="text-sm font-medium">
                  {crossSheetMode
                    ? (t("crossSheetActive") || 'Cross-sheet linking…')
                    : (t("crossSheetLink") || 'Cross-sheet link')}
                </span>
              </button>
            )}
          </div>
        </div>

        {hasCollapsedGroups && (
          <div className={cn(mobile ? "p-2" : "p-4", "border-b border-brand-navy/10")} data-guide="layer-tree">
            <Collapsible open={layersOpen} onOpenChange={setLayersOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full text-xs font-semibold uppercase tracking-wider mb-3 transition-colors" style={{ color: NAVY, opacity: 0.5 }}>
                {layersOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                <Layers className="w-3 h-3" />
                Layers
              </CollapsibleTrigger>
              <CollapsibleContent>
                <LayerTree
                  groups={groups}
                  blocks={blocks}
                  currentGroupId={currentLayerGroupId}
                  onNavigate={onNavigateToLayer}
                />
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {blocks.length > 0 && (
          <div className={cn(mobile ? "p-2" : "p-4", "border-b border-brand-navy/10")}>
            <Collapsible open={blocksOpen} onOpenChange={setBlocksOpen}>
              <CollapsibleTrigger className="flex items-center gap-2 w-full text-xs font-semibold uppercase tracking-wider mb-3 transition-colors" style={{ color: NAVY, opacity: 0.5 }}>
                {blocksOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {t("blocks")} ({filteredBlocks.length}{normalizedQuery ? `/${blocks.length}` : ''})
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className={cn("space-y-1.5", mobile && "space-y-0.5")}>
                  {filteredBlocks.length === 0 && normalizedQuery && (
                    <p className="text-xs text-center py-2" style={{ color: NAVY, opacity: 0.3 }}>No matching blocks</p>
                  )}
                  {filteredBlocks.map((block) => (
                    <div
                      key={block.id}
                      className={cn(
                        "flex items-center gap-2 rounded-lg cursor-pointer transition-colors",
                        mobile ? "px-1.5 py-1" : "px-2 py-1.5",
                        selectedBlockId === block.id ? "bg-brand-navy/20" : "bg-brand-navy/5 hover:bg-brand-navy/10",
                      )}
                      style={{ color: NAVY }}
                      onClick={() => onSelectBlock(block.id)}
                    >
                      <ColorPicker color={block.color} onChange={(color) => onUpdateBlockColor(block.id, color)} />
                      <span className={cn("font-medium truncate flex-1", mobile ? "text-[10px]" : "text-xs")}>{block.config.name}</span>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {groups.length > 0 && (
          <div className={cn(mobile ? "p-2" : "p-4", "border-b border-brand-navy/10")}>
            <Collapsible open={groupsOpen} onOpenChange={setGroupsOpen}>
              <div className="flex items-center justify-between mb-3">
                <CollapsibleTrigger className="flex items-center gap-2 flex-1 text-xs font-semibold uppercase tracking-wider transition-colors" style={{ color: NAVY, opacity: 0.5 }}>
                  {groupsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  {t("groups")} ({filteredGroups.length}{normalizedQuery ? `/${groups.length}` : ''})
                </CollapsibleTrigger>
                {(onCollapseAll || onCollapseToLayers || onExpandAll) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn("hover:bg-brand-navy/10 ml-1", mobile ? "h-5 w-5" : "h-6 w-6")}
                        title={t("groupBatchActions")}
                        aria-label={t("groupBatchActions")}
                        data-testid="group-batch-actions"
                      >
                        <MoreHorizontal className="w-3.5 h-3.5" style={{ color: NAVY, opacity: 0.6 }} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      {onCollapseAll && (
                        <DropdownMenuItem onSelect={onCollapseAll} data-testid="menu-collapse-all">
                          <FoldVertical className="w-4 h-4 mr-2" />
                          {t("collapseAll")}
                        </DropdownMenuItem>
                      )}
                      {onCollapseToLayers && (
                        <DropdownMenuItem onSelect={onCollapseToLayers} data-testid="menu-collapse-to-layers">
                          <Layers className="w-4 h-4 mr-2" />
                          {t("collapseToLayers")}
                        </DropdownMenuItem>
                      )}
                      {onExpandAll && (
                        <DropdownMenuItem onSelect={onExpandAll} data-testid="menu-expand-all">
                          <UnfoldVertical className="w-4 h-4 mr-2" />
                          {t("expandAll")}
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
              <CollapsibleContent>
                <div className={cn("space-y-1.5", mobile && "space-y-0.5")}>
                  {filteredGroups.length === 0 && normalizedQuery && (
                    <p className="text-xs text-center py-2" style={{ color: NAVY, opacity: 0.3 }}>No matching groups</p>
                  )}
                  {filteredGroups.map((group, index) => {
                    const groupBlocks = blocks.filter((b) => group.blockIds.includes(b.id));
                    return (
                      <div
                        key={group.id}
                        className={cn(
                          "flex items-center gap-2 rounded-lg bg-brand-navy/5",
                          mobile ? "px-1.5 py-1" : "px-2 py-1.5",
                        )}
                        style={{ color: NAVY }}
                      >
                        <ColorPicker color={group.color} onChange={(color) => onUpdateGroupColor(group.id, color)} />
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className={cn("font-medium truncate flex items-center gap-1", mobile ? "text-[10px]" : "text-xs")}>
                            {group.name || `${t("groups")} ${index + 1}`}
                            {groupBlocks.length === 0 && (
                              <span className="text-[9px] font-normal px-1 py-0 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400">
                                empty layer
                              </span>
                            )}
                          </span>
                          <span className={cn("truncate", mobile ? "text-[8px]" : "text-[10px]")} style={{ opacity: 0.5 }}>
                            {groupBlocks.length} {t("blocks").toLowerCase()}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("hover:bg-brand-navy/10", mobile ? "h-5 w-5" : "h-6 w-6")}
                          onClick={() => onToggleGroupCollapse(group.id)}
                          title={group.collapsed ? "Expand group" : "Collapse into layer"}
                        >
                          <Layers className={cn("w-3 h-3", group.collapsed ? "text-[#0088ff]" : "")} style={group.collapsed ? undefined : { color: NAVY, opacity: 0.5 }} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("hover:bg-brand-navy/10", mobile ? "h-5 w-5" : "h-6 w-6")}
                          onClick={() => onToggleGroupLock(group.id)}
                          title={group.locked ? t("unlockGroup") : t("lockGroup")}
                        >
                          {group.locked ? (
                            <Lock className="w-3 h-3 text-[#0088ff]" />
                          ) : (
                            <Unlock className="w-3 h-3" style={{ color: NAVY, opacity: 0.5 }} />
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </ScrollArea>

      <div className="p-4 border-t border-brand-navy/10">
        <p className="text-xs text-center" style={{ color: NAVY, opacity: 0.4 }}>{t("dragToCanvas")}</p>
      </div>
    </aside>
  );
}
